import type {
  InterpolationValue,
  InterpolationValues,
  Plural,
  Translatable,
  TranslateOptions,
  Translation,
  TranslationWarningHandler,
} from './types';

const fallbackMessage = '';

interface RuntimeTranslateOptions extends TranslateOptions {
  showKeys?: boolean;
  onWarning?: TranslationWarningHandler;
}

interface GetMessageOptions {
  count?: number;
  onWarning?: TranslationWarningHandler;
}

export const isTranslatable = (toCheck: unknown): toCheck is Translatable =>
  typeof toCheck === 'object' &&
  toCheck !== null &&
  (toCheck as Translatable).message !== undefined;

export const isPlural = (toCheck: unknown): toCheck is Plural => {
  const possiblePlural = toCheck as Plural;
  return (
    possiblePlural?.zero !== undefined ||
    possiblePlural?.one !== undefined ||
    possiblePlural?.other !== undefined
  );
};

export function getMessage(
  text: Translatable,
  { count, onWarning }: GetMessageOptions = {}
): string {
  if (isPlural(text.message)) {
    if (count === undefined) {
      onWarning?.({ type: 'plural-count-missing', text });
    }

    let message;
    switch (count) {
      case 0:
        message = text.message.zero || text.message.other;
        break;
      case 1:
      case -1:
        message = text.message.one || text.message.other;
        break;
      default:
        message = text.message.other;
    }

    if (!message) {
      onWarning?.({ type: 'plural-form-missing', text, count });
    }

    return message || fallbackMessage;
  }

  if (count !== undefined) {
    onWarning?.({ type: 'count-without-plural', text, count });
  }

  return text.message;
}

export function interpolateValues(
  message: string,
  values: InterpolationValues = {},
  onWarning?: TranslationWarningHandler
) {
  Object.entries(values).forEach(([key, value]) => {
    message = message.replaceAll(
      `{{${key}}}`,
      interpolationValueToString(value, onWarning)
    );
  });

  return message;
}

export function translate(
  text: Translation,
  { values, count, showKeys = false, onWarning }: RuntimeTranslateOptions = {}
): string | undefined {
  if (!isTranslatable(text)) {
    return text ?? undefined;
  }

  if (showKeys) {
    return text.key || `missing key for ${messageToString(text.message)}`;
  }

  const message = getMessage(text, { count, onWarning });

  return interpolateValues(message, { count, ...values }, onWarning);
}

function interpolationValueToString(
  value: InterpolationValue,
  onWarning?: TranslationWarningHandler
) {
  if (isTranslatable(value)) {
    return getMessage(value, { onWarning });
  }
  if (typeof value === 'number') {
    return `${value}`;
  }
  if (typeof value === 'undefined' || value === null) {
    return '';
  }
  return value;
}

function messageToString(message: Translatable['message']) {
  return typeof message === 'string' ? message : JSON.stringify(message);
}
